import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminUserDetails } from '@/hooks/useAdminUserDetails';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import LoadingSpinner from '@/components/LoadingSpinner';
import { ArrowLeft, Ban, CheckCircle, RefreshCw, Trash2, Save } from 'lucide-react';
import { format } from 'date-fns';

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const {
    user,
    activityLogs,
    adminActions,
    loading,
    updateUserPlan,
    updateUserCredits,
    updateDailyLimit,
    banUser,
    unbanUser,
    resetUsage,
    updateNotes,
    deleteUser
  } = useAdminUserDetails(userId);

  const [editMode, setEditMode] = useState(false);
  const [planValue, setPlanValue] = useState('');
  const [creditsValue, setCreditsValue] = useState('');
  const [limitValue, setLimitValue] = useState('');
  const [notesValue, setNotesValue] = useState('');
  const [banReason, setBanReason] = useState('');
  const [showBanDialog, setShowBanDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading user details..." />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card>
          <CardContent className="pt-6">
            <p>User not found</p>
            <Button onClick={() => navigate('/admin')} className="mt-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Admin
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSaveChanges = async () => {
    if (planValue && planValue !== user.plan) {
      await updateUserPlan(planValue);
    }
    if (creditsValue && Number(creditsValue) !== user.total_credits) {
      await updateUserCredits(Number(creditsValue));
    }
    if (limitValue && Number(limitValue) !== user.daily_generation_limit) {
      await updateDailyLimit(Number(limitValue));
    }
    if (notesValue !== user.notes) {
      await updateNotes(notesValue);
    }
    setEditMode(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/60">
                User Details
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {user.email}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user.is_banned ? (
              <Badge variant="destructive">Banned</Badge>
            ) : (
              <Badge variant="default">Active</Badge>
            )}
            <Badge variant="secondary" className="capitalize">
              {user.role}
            </Badge>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - User Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Account Information</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (editMode) {
                        setEditMode(false);
                      } else {
                        setEditMode(true);
                        setPlanValue(user.plan || '');
                        setCreditsValue(String(user.total_credits || 0));
                        setLimitValue(String(user.daily_generation_limit));
                        setNotesValue(user.notes || '');
                      }
                    }}
                  >
                    {editMode ? 'Cancel' : 'Edit'}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input value={user.full_name || 'N/A'} disabled />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={user.email} disabled />
                  </div>
                  <div>
                    <Label>Subscription Plan</Label>
                    {editMode ? (
                      <Select value={planValue} onValueChange={setPlanValue}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="basic">Basic</SelectItem>
                          <SelectItem value="pro">Pro</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={user.plan || 'free'} disabled />
                    )}
                  </div>
                  <div>
                    <Label>Total Credits</Label>
                    <Input
                      type="number"
                      value={editMode ? creditsValue : user.total_credits || 0}
                      onChange={(e) => setCreditsValue(e.target.value)}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <Label>Daily Generation Limit</Label>
                    <Input
                      type="number"
                      value={editMode ? limitValue : user.daily_generation_limit}
                      onChange={(e) => setLimitValue(e.target.value)}
                      disabled={!editMode}
                    />
                  </div>
                  <div>
                    <Label>Account Created</Label>
                    <Input value={format(new Date(user.created_at), 'PPP')} disabled />
                  </div>
                </div>

                <div>
                  <Label>Admin Notes</Label>
                  <Textarea
                    value={editMode ? notesValue : (user.notes || 'No notes')}
                    onChange={(e) => setNotesValue(e.target.value)}
                    disabled={!editMode}
                    rows={3}
                  />
                </div>

                {editMode && (
                  <Button onClick={handleSaveChanges} className="w-full">
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Usage Statistics */}
            <Card>
              <CardHeader>
                <CardTitle>Usage Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Images Generated</p>
                    <p className="text-2xl font-bold">{user.monthly_images_used}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Scripts Generated</p>
                    <p className="text-2xl font-bold">{user.monthly_scripts_used}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Audio Minutes</p>
                    <p className="text-2xl font-bold">{user.monthly_audio_minutes_used}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full mt-4"
                  onClick={() => resetUsage()}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reset Usage Statistics
                </Button>
              </CardContent>
            </Card>

            {/* Activity Logs */}
            <Card>
              <CardHeader>
                <CardTitle>Activity & Admin Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="activity">
                  <TabsList className="w-full">
                    <TabsTrigger value="activity" className="flex-1">User Activity</TabsTrigger>
                    <TabsTrigger value="admin" className="flex-1">Admin Actions</TabsTrigger>
                  </TabsList>
                  <TabsContent value="activity" className="space-y-2 mt-4">
                    {activityLogs.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">No activity logs</p>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {activityLogs.map((log) => (
                          <div key={log.id} className="p-3 bg-muted/50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <span className="font-medium capitalize">{log.activity_type}</span>
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(log.created_at), 'PPp')}
                              </span>
                            </div>
                            {log.description && (
                              <p className="text-sm text-muted-foreground mt-1">{log.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                  <TabsContent value="admin" className="space-y-2 mt-4">
                    {adminActions.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">No admin actions</p>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {adminActions.map((action) => (
                          <div key={action.id} className="p-3 bg-muted/50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <span className="font-medium capitalize">{action.action_type}</span>
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(action.created_at), 'PPp')}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              By: {action.admin_email || 'System'}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {user.is_banned ? (
                  <Button
                    variant="default"
                    className="w-full"
                    onClick={() => unbanUser()}
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Unban User
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={() => setShowBanDialog(true)}
                  >
                    <Ban className="mr-2 h-4 w-4" />
                    Ban User
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => resetUsage()}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reset Usage
                </Button>

                <Button
                  variant="destructive"
                  className="w-full"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete User
                </Button>
              </CardContent>
            </Card>

            {user.is_banned && (
              <Card className="border-destructive">
                <CardHeader>
                  <CardTitle className="text-destructive">Ban Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Banned At:</span>{' '}
                      {user.banned_at ? format(new Date(user.banned_at), 'PPp') : 'N/A'}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Reason:</span>{' '}
                      {user.banned_reason || 'No reason provided'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Ban Dialog */}
      <AlertDialog open={showBanDialog} onOpenChange={setShowBanDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ban User</AlertDialogTitle>
            <AlertDialogDescription>
              Please provide a reason for banning this user. This action can be reversed later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="Reason for ban..."
            value={banReason}
            onChange={(e) => setBanReason(e.target.value)}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                banUser(banReason);
                setShowBanDialog(false);
                setBanReason('');
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Ban User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the user account and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                await deleteUser();
                setShowDeleteDialog(false);
                navigate('/admin');
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserDetailPage;
